import EmptyState from './EmptyState'
import TableContent from './TableContent'
import RenameDialog from './RenameDialog'
import { useState, useEffect } from 'react'
import TablePagination from './TablePagination'
import { CubeLoader } from '@/components/ui/cube-loader'
import { useProcessedForms } from './hooks/useProcessedForms'
import { useTableColumns } from './hooks/useTableColumns'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'

const PAGE_SIZE = 5

/**
 * LeadFormTable Component
 *
 * A table component for displaying and managing lead forms with features like:
 * - Row selection
 * - Sorting
 * - Pagination
 * - Form actions (edit, archive, duplicate, unarchive)
 * - Loading states
 * - Empty states
 * - Hover actions with pencil and delete buttons
 */
const LeadFormTable = ({
    forms,
    loading,
    externalSelectedForm,
    onRowClick,
    readonly,
    onArchive,
    onDuplicate,
    onUnarchive,
    onEdit,
    onRefresh,
}) => {
    const [sorting, setSorting] = useState([])
    const processedForms = useProcessedForms(forms)
    const [isArchiving, setIsArchiving] = useState(false)
    const [isUnarchiving, setIsUnarchiving] = useState(false)
    const [isDuplicating, setIsDuplicating] = useState(false)
    const [selectedForm, setSelectedForm] = useState(() => externalSelectedForm || null)
    const [renameDialog, setRenameDialog] = useState({ show: false, form: null, newName: '' })
    const [archiveConfirmation, setArchiveConfirmation] = useState({ show: false, form: null })
    const [unarchiveConfirmation, setUnarchiveConfirmation] = useState({ show: false, form: null })
    const [duplicateConfirmation, setDuplicateConfirmation] = useState({ show: false, form: null })

    const handleRowClick = (form) => {
        if (readonly) return
        setSelectedForm(form)
        if (onRowClick) onRowClick(form)
    }

    const handleConfirmArchive = async () => {
        if (!archiveConfirmation.form) return

        setIsArchiving(true)
        try {
            await onArchive?.(archiveConfirmation.form)
            setArchiveConfirmation({ show: false, form: null })
        } catch (error) {
            console.error('Error archiving form:', error)
        } finally {
            setIsArchiving(false)
        }
    }

    const handleCancelArchive = () => {
        setArchiveConfirmation({ show: false, form: null })
    }

    const handleConfirmUnarchive = async () => {
        if (!unarchiveConfirmation.form) return

        setIsUnarchiving(true)
        try {
            await onUnarchive?.(unarchiveConfirmation.form)
            setUnarchiveConfirmation({ show: false, form: null })
        } finally {
            setIsUnarchiving(false)
        }
    }

    const handleArchiveRequest = (form) => setArchiveConfirmation({ show: true, form })
    const handleDuplicateRequest = (form) => setDuplicateConfirmation({ show: true, form })
    const handleUnarchiveRequest = (form) => setUnarchiveConfirmation({ show: true, form })
    const handleCancelUnarchive = () => setUnarchiveConfirmation({ show: false, form: null })
    const handleCancelDuplicate = () => setDuplicateConfirmation({ show: false, form: null })
    const handleCancelRename = () => setRenameDialog({ show: false, form: null, newName: '' })
    const handleRenameRequest = (form) => setRenameDialog({ show: true, form, newName: form.name })
    const handleRenameInputChange = (e) => setRenameDialog((prev) => ({ ...prev, newName: e.target.value }))

    const handleConfirmDuplicate = async () => {
        if (!duplicateConfirmation.form) return

        setIsDuplicating(true)

        try {
            await onDuplicate?.(duplicateConfirmation.form)
            setDuplicateConfirmation({ show: false, form: null })
        } finally {
            setIsDuplicating(false)
        }
    }

    const handleConfirmRename = async () => {
        if (!renameDialog.form || !renameDialog.newName.trim()) return

        setRenameDialog({ show: false, form: null, newName: '' })
    }

    // Get table columns configuration
    const columns = useTableColumns({
        selectedForm,
        readonly,
        onFormClick: handleRowClick,
        onDuplicate: handleDuplicateRequest,
        onArchive: handleArchiveRequest,
        onUnarchive: handleUnarchiveRequest,
        onEdit: onEdit || handleRenameRequest,
    })

    // Initialize React Table
    const table = useReactTable({
        data: processedForms,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        state: {
            sorting,
        },
        initialState: {
            pagination: {
                pageSize: PAGE_SIZE,
            },
        },
    })

    // Sync external selection with internal state
    useEffect(() => {
        if (externalSelectedForm?.id !== selectedForm?.id) {
            setSelectedForm(externalSelectedForm)
        }
    }, [externalSelectedForm, loading])

    // Render loading state
    if (loading)
        return (
            <div className="tw:w-full tw:flex tw:items-start tw:justify-center">
                <CubeLoader size="default" />
            </div>
        )
    if (!forms || forms.length === 0) {
        setTimeout(() => {
            return <EmptyState onRefresh={onRefresh} />
        }, 1)
    }

    // Main table render
    return (
        <div className="tw:border-t">
            <div className="overflow-auto">
                <TableContent
                    table={table}
                    selectedForm={selectedForm}
                    onRowClick={handleRowClick}
                    readonly={readonly}
                />
            </div>

            <TablePagination table={table} />

            {/* Archive confirmation dialog */}
            <ConfirmationDialog
                isOpen={archiveConfirmation.show}
                onClose={handleCancelArchive}
                onConfirm={handleConfirmArchive}
                title="Archive Form"
                description="Are you sure you want to archive this form? You can restore it later from the archived forms section."
                isProcessing={isArchiving}
            />

            {/* Unarchive confirmation dialog */}
            <ConfirmationDialog
                isOpen={unarchiveConfirmation.show}
                onClose={handleCancelUnarchive}
                onConfirm={handleConfirmUnarchive}
                title="Restore Form"
                description="Are you sure you want to restore this form? It will be moved back to the active forms section."
                isProcessing={isUnarchiving}
            />

            {/* Duplicate confirmation dialog */}
            <ConfirmationDialog
                isOpen={duplicateConfirmation.show}
                onClose={handleCancelDuplicate}
                onConfirm={handleConfirmDuplicate}
                title="Duplicate Form"
                description="Are you sure you want to duplicate this form? A new form will be created with the same content."
                variant="default"
                isProcessing={isDuplicating}
            />

            {/* Rename dialog */}
            <RenameDialog
                isOpen={renameDialog.show}
                formName={renameDialog.newName}
                onClose={handleCancelRename}
                onConfirm={handleConfirmRename}
                onChange={handleRenameInputChange}
            />
        </div>
    )
}

export default LeadFormTable
